/**
 * Azure Entra ID Authentication Context using MSAL - DUAL TOKEN STRATEGY
 * Complete separation of Graph API tokens (user info) and Backend API tokens (API calls)
 * Preserves all existing functionality while fixing user information display
 */

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { msalConfig } from '../config/msalConfig';
import { User } from '../types';
import { initPermissions } from '../lib/auth/initPermissions';
import { getCurrentConfig, getApiScope } from '../config';
import { universalApiService } from '../services/universalApiService';
import { apiService } from '../services/api';


// Create single MSAL instance to prevent conflicts
export const msalInstance = new PublicClientApplication(msalConfig);

// Expose MSAL instance on window for debugging (development only)
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  (window as any).msalInstance = msalInstance;
}

// Global interaction state to prevent conflicts
let globalInteractionInProgress = false;

// NOTE: MSAL initialization is now handled in useAppInitialization hook
// before any components mount, so we can assume it's ready here

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean | null;
  isLoading: boolean;
  error: string | undefined;
  backendAuthEnabled: boolean | null; // New: backend auth status
  login: () => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  getAccessToken: () => Promise<string | null>;
  getGraphToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isInteractionInProgress, setIsInteractionInProgress] = useState(false);
  
  // Local auth state - start with loading to prevent flash
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [backendAuthEnabled, setBackendAuthEnabled] = useState<boolean | null>(null);
  

  const interactionInProgressRef = useRef(false);

  // MOVED UP: Get access token for API calls - trust MSAL state
  const getAccessToken = async (): Promise<string | null> => {
    // getAccessToken function called
    
    try {
      const accounts = msalInstance.getAllAccounts();
      // getAccessToken called - accounts available
      
      if (accounts.length === 0) {
        // No accounts available for token acquisition
        return null;
      }

      const tokenRequest = {
        scopes: [getApiScope()], // Use scope from .env
        account: accounts[0]
      };

      // Attempting silent token acquisition for backend API
      
      const response = await msalInstance.acquireTokenSilent(tokenRequest);
      // Backend API token acquired successfully

      return response.accessToken;
    } catch (error) {
      console.error('❌ Failed to get backend API access token:', error);
      if (error instanceof Error) {
        console.error('❌ Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }
      return null;
    }
  };

  // MOVED UP: Get Graph API token - trust MSAL state
  const getGraphToken = async (): Promise<string | null> => {
    try {
      const accounts = msalInstance.getAllAccounts();
      // getGraphToken called - accounts available
      
      if (accounts.length === 0) {
        // No accounts available for Graph token
        return null;
      }

      const tokenRequest = {
        scopes: ['User.Read', 'GroupMember.Read.All'],
        account: accounts[0]
      };

      // Attempting silent Graph token acquisition
      const response = await msalInstance.acquireTokenSilent(tokenRequest);
      // Graph token acquired successfully

      return response.accessToken;
    } catch (error) {
      console.error('❌ Failed to get Graph token:', error);
      return null;
    }
  };

  // Initialize authentication based on configuration (no backend calls)
  useEffect(() => {
    const initializeAuth = async () => {
      const config = getCurrentConfig();
      
      try {
        // Use frontend configuration only - no backend API calls
        const frontendAuthEnabled = config.auth.enableAuthentication;
        setBackendAuthEnabled(frontendAuthEnabled);
        
        // Authentication configuration available
        
        if (!frontendAuthEnabled) {
          // Authentication disabled - setting authenticated state
          setIsAuthenticated(true);
          setUser(null); // No user object needed when auth is disabled
          setIsLoading(false);
          return; // Skip MSAL initialization
        } else {
          // Authentication enabled - proceeding with MSAL initialization
          // Continue to MSAL initialization
        }
        
      } catch (error) {
        console.error('❌ Failed to initialize authentication:', error);
        // Default to requiring authentication for safety
        setBackendAuthEnabled(true);
        // Defaulting to authentication required due to error
      }
    };
    
    initializeAuth();
  }, []);

  // Initialize MSAL (only when authentication is required)
  useEffect(() => {
    // Only setup MSAL if backend auth is enabled
    if (backendAuthEnabled === null || backendAuthEnabled === false) {
      return; // Wait for auth status or skip if auth disabled
    }
    
    const setupMsal = async () => {
      try {
        // MSAL is already initialized by useAppInitialization hook
        // Just need to handle existing accounts and redirects
        
        // Handle any pending redirect response
        try {
          // Checking for pending redirect response
          const response = await msalInstance.handleRedirectPromise();
          if (response) {
            // Redirect response received
            if (response.account) {
              // Converting account to user
              const user = await convertAccountToUser(response.account);
              // User converted successfully
              setUser(user);
              setIsAuthenticated(true);
              setIsLoading(false);
            }
          } else {
            // No pending redirect response
          }
        } catch (error) {
          console.error('❌ Error handling redirect:', error);
          
          // Check if this is an authorization error
          if (error instanceof Error && error.message.includes('not authorized to access this system')) {
            setError('You are not authorized to access this system. Your Azure AD groups do not match any configured user groups for this environment. If you believe this is an error, please contact your administrator.');
            setIsAuthenticated(false);
            setIsLoading(false);
            return; // Stop processing
          }
          
          // Handle other redirect errors silently and continue
        }
        
        // Check for existing accounts (simplified like test app)
        const accounts = msalInstance.getAllAccounts();
        // Found existing accounts
        
        if (accounts.length > 0) {
          try {
            // Using existing account
            const user = await convertAccountToUser(accounts[0]);
            // User converted successfully
            setUser(user);
            setIsAuthenticated(true);
            setIsLoading(false);
          } catch (error) {
            console.error('❌ Error converting account to user:', error);
            
            // Check if this is an authorization error
            if (error instanceof Error && error.message.includes('not authorized to access this system')) {
              setError('You are not authorized to access this system. Your Azure AD groups do not match any configured user groups for this environment. If you believe this is an error, please contact your administrator.');
              setIsAuthenticated(false);
              setIsLoading(false);
              return; // Stop processing
            }
            
            // For other errors, set authenticated to false and continue
            setIsAuthenticated(false);
            setIsLoading(false);
          };
        } else {
          // No existing accounts found
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('MSAL setup failed:', error);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };

    setupMsal();
  }, [backendAuthEnabled]);

  // Handle post-logout redirect
  useEffect(() => {
    // Check if we're returning from a logout and need to redirect to login
    if (!isLoading && isAuthenticated === false) {
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/') {
        // Only redirect if we're not already on login page and not in the middle of authentication
        // This prevents redirect loops during permission initialization
        if (!isInteractionInProgress) {
          window.location.href = '/login';
        }
      }
    }
  }, [isLoading, isAuthenticated, isInteractionInProgress]);

  // Cleanup effect to reset interaction states
  useEffect(() => {
    return () => {
      globalInteractionInProgress = false;
      interactionInProgressRef.current = false;
    };
  }, []);

  // Convert MSAL account to User object
  const convertAccountToUser = async (account: AccountInfo): Promise<User> => {
    // Starting account conversion
    
    // STEP 1: Get basic user info from ID token
    const basicUserInfo = {
      username: account.username || account.name || 'Unknown User',
      fullName: account.name || account.username || 'Unknown User',
      email: account.username || null
    };
    
    // Basic user info available

    // STEP 2: Get access token for Graph API
    try {
      // Getting Graph token
      const graphToken = await getGraphToken();
      let userGroups = [];
      
      if (graphToken) {
        // Graph token obtained, fetching user groups
        // STEP 3: Use Graph API token to get user groups for RBAC
        const { callMsGraphGroups } = await import('../services/graphService');
        const userGroupsResponse = await callMsGraphGroups(graphToken);
        userGroups = userGroupsResponse.value || [];
        // User groups from Graph API available
      } else {
        // No Graph token available, proceeding without Graph API groups
        // No Graph token available, proceeding without Graph API groups
      }
      
      // STEP 4: Initialize dynamic group-based permissions system
      // This MUST be called regardless of Graph token availability
      // Initializing permissions
      // Passing Graph API groups to initPermissions
      
      // FIXED: Pass Graph API groups to initPermissions
      await initPermissions(true, getAccessToken, account, userGroups);
      // Permissions initialized successfully
      
      // Store groups in user object for permission store
      const user = {
        id: parseInt(account.localAccountId.substring(0, 8), 16),
        username: basicUserInfo.username,
        fullName: basicUserInfo.fullName,
        email: basicUserInfo.email,
        isActive: true,
        lastLogin: new Date().toISOString(),
        createdDate: new Date().toISOString(),
        groups: userGroups
      };
      
      // User object created
      return user;
    } catch (error) {
      console.error('❌ Failed to get user groups:', error);
      
      // Check if this is an authorization error (user not allowed in this environment)
      if (error instanceof Error && error.message.includes('not authorized to access this system')) {
        // This is an authorization error - user doesn't have proper groups for this environment
        // Don't try any fallback, just throw the error up to be handled by the UI
        throw error;
      }
      
      // For other errors (network issues, Graph API failures, etc.), try fallback
      console.log('Attempting fallback initialization due to technical error');
      
      // Initialize permissions without Graph API groups (will use fallback)
      await initPermissions(true, getAccessToken, account, []);
      
      // Silent fail - use fallback user info
      const user = {
        id: parseInt(account.localAccountId.substring(0, 8), 16),
        username: basicUserInfo.username,
        fullName: basicUserInfo.fullName,
        email: basicUserInfo.email,
        isActive: true,
        lastLogin: new Date().toISOString(),
        createdDate: new Date().toISOString(),
        groups: []
      };
      
      return user;
    }
  };

  // Handle login process with interaction protection
  const handleLogin = async () => {
    // Starting login process
    
    if (globalInteractionInProgress || interactionInProgressRef.current) {
      // Login already in progress, skipping
      return;
    }

    try {
      // Setting up login request
      globalInteractionInProgress = true;
      interactionInProgressRef.current = true;
      setIsLoading(true);
      setError(undefined);
      setIsInteractionInProgress(true);
      
      const loginRequest = {
        scopes: [
          'User.Read', 
          'GroupMember.Read.All', 
          'openid', 
          'profile', 
          'email',
          getApiScope()  // Backend API scope from .env
        ],
        prompt: 'select_account'
      };

      // Initiating MSAL login redirect
      // Use Azure AD login
      await msalInstance.loginRedirect(loginRequest);
      // Login redirect initiated successfully
      
      // Note: The rest of the login logic will be handled when the page redirects back
    } catch (error) {
      console.error('❌ Login failed:', error);
      
      // Handle interaction_in_progress error specifically
      if (error instanceof Error && error.message.includes('interaction_in_progress')) {
        // Interaction in progress error - will retry
        globalInteractionInProgress = false;
        interactionInProgressRef.current = false;
        setIsInteractionInProgress(false);
        
        // Wait a bit and retry
        setTimeout(() => {
          handleLogin();
        }, 1000);
        return;
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      console.error('💥 Setting error state:', errorMessage);
      setError(errorMessage);
      setIsLoading(false);
    } finally {
      globalInteractionInProgress = false;
      interactionInProgressRef.current = false;
      setIsInteractionInProgress(false);
    }
  };

  // Handle logout process with interaction protection
  const handleLogout = async () => {
    if (globalInteractionInProgress || interactionInProgressRef.current) {
      return;
    }

    try {
      globalInteractionInProgress = true;
      interactionInProgressRef.current = true;
      setIsInteractionInProgress(true);
      
      // Clear threshold cache and stop periodic refresh
      const { ThresholdServiceStatic } = await import('../services/thresholdService');
      const { OccupancyServiceStatic } = await import('../services/occupancyService');
      ThresholdServiceStatic.stopPeriodicRefresh();
      ThresholdServiceStatic.clearCache();
      OccupancyServiceStatic.clearAll();
      
      // Clear local state first
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear any stored tokens or session data
      sessionStorage.clear();
      localStorage.removeItem('msal.token');
      
      // Use logoutRedirect to ensure proper cleanup
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/login'
      });
      
      // Fallback: if MSAL logout doesn't redirect properly, force redirect after a delay
      setTimeout(() => {
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }, 2000);
      
    } catch (error) {
      console.error('Logout failed:', error);
      
      // Handle interaction_in_progress error specifically
      if (error instanceof Error && error.message.includes('interaction_in_progress')) {
        globalInteractionInProgress = false;
        interactionInProgressRef.current = false;
        setIsInteractionInProgress(false);
        
        // Wait a bit and retry
        setTimeout(() => {
          handleLogout();
        }, 1000);
        return;
      }
      
      // If logout fails, still clear local state and redirect
      setUser(null);
      setIsAuthenticated(false);
      sessionStorage.clear();
      localStorage.removeItem('msal.token');
      window.location.href = '/login';
    } finally {
      globalInteractionInProgress = false;
      interactionInProgressRef.current = false;
      setIsInteractionInProgress(false);
    }
  };



  // Context value
  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    backendAuthEnabled,
    login: handleLogin,
    logout: handleLogout,
    clearError: () => setError(undefined),
    getAccessToken,
    getGraphToken,
  };

  // No loading screen needed here since MSAL initialization 
  // is handled upfront in useAppInitialization

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
